import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Calendar, Plus, Save, Trash2, Edit2, Users, Bell,
  Clock, CheckCircle2, AlertTriangle, FileText,
  MessageSquare, Target, Sparkles, User
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Team members with titles
const TEAM_MEMBERS = [
  { name: 'Renee', title: 'Operations Manager | Exec Assistant', emoji: '👩‍💼' },
  { name: 'Biz', title: 'Business Partner | Director of VTC', emoji: '👨‍💼' },
  { name: 'Ozzy', title: 'CEO | Owner', emoji: '👑' }
];

const MeetingNotes = () => {
  const [meetings, setMeetings] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('meetings');
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [editingFollowUp, setEditingFollowUp] = useState(null);
  
  // Form state for new meeting
  const [meetingForm, setMeetingForm] = useState({
    date: getNextWednesday(),
    facilitator: 'Renee',
    notes: '',
    agenda_items: [],
    action_items: []
  });

  // Form state for follow-up
  const [followUpForm, setFollowUpForm] = useState({
    assigned_to: 'Renee',
    item: '',
    due_date: getNextWednesday(),
    status: 'pending'
  });

  // Get next Wednesday
  function getNextWednesday() {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilWednesday = (3 - dayOfWeek + 7) % 7 || 7;
    const nextWed = new Date(today);
    nextWed.setDate(today.getDate() + daysUntilWednesday);
    return nextWed.toISOString().split('T')[0];
  }

  // Check if tomorrow is meeting day (Tuesday reminder)
  const isTuesday = new Date().getDay() === 2;
  const isWednesday = new Date().getDay() === 3;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load from localStorage for now (can be moved to backend later)
      const savedMeetings = localStorage.getItem('vyrus_meetings');
      const savedFollowUps = localStorage.getItem('vyrus_followups');
      
      if (savedMeetings) setMeetings(JSON.parse(savedMeetings));
      if (savedFollowUps) setFollowUps(JSON.parse(savedFollowUps));
    } catch (error) {
      console.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const saveMeetings = (newMeetings) => {
    setMeetings(newMeetings);
    localStorage.setItem('vyrus_meetings', JSON.stringify(newMeetings));
  };

  const saveFollowUps = (newFollowUps) => {
    setFollowUps(newFollowUps);
    localStorage.setItem('vyrus_followups', JSON.stringify(newFollowUps));
  };

  const handleAddMeeting = () => {
    const newMeeting = {
      id: Date.now().toString(),
      ...meetingForm,
      created_at: new Date().toISOString()
    };
    
    const updatedMeetings = [newMeeting, ...meetings];
    saveMeetings(updatedMeetings);
    setDialogOpen(false);
    resetMeetingForm();
    toast.success('Meeting notes created! 📝');
  };

  const handleUpdateMeeting = () => {
    const updatedMeetings = meetings.map(m => 
      m.id === editingMeeting.id ? { ...m, ...meetingForm } : m
    );
    saveMeetings(updatedMeetings);
    setDialogOpen(false);
    setEditingMeeting(null);
    resetMeetingForm();
    toast.success('Meeting notes updated! ✅');
  };

  const handleDeleteMeeting = (id) => {
    if (!window.confirm('Delete this meeting?')) return;
    const updatedMeetings = meetings.filter(m => m.id !== id);
    saveMeetings(updatedMeetings);
    setSelectedMeeting(null);
    toast.success('Meeting deleted');
  };

  const handleAddFollowUp = () => {
    if (editingFollowUp) {
      // Update existing
      const updatedFollowUps = followUps.map(f => 
        f.id === editingFollowUp.id ? { ...f, ...followUpForm } : f
      );
      saveFollowUps(updatedFollowUps);
      setEditingFollowUp(null);
      toast.success('Follow-up updated! ✅');
    } else {
      // Add new
      const newFollowUp = {
        id: Date.now().toString(),
        ...followUpForm,
        created_at: new Date().toISOString()
      };
      const updatedFollowUps = [newFollowUp, ...followUps];
      saveFollowUps(updatedFollowUps);
      toast.success('Follow-up item added! 🎯');
    }
    setFollowUpDialogOpen(false);
    resetFollowUpForm();
  };

  const openEditFollowUp = (followUp) => {
    setEditingFollowUp(followUp);
    setFollowUpForm({
      assigned_to: followUp.assigned_to,
      item: followUp.item,
      due_date: followUp.due_date,
      status: followUp.status
    });
    setFollowUpDialogOpen(true);
  };

  const toggleFollowUpStatus = (id) => {
    const updatedFollowUps = followUps.map(f => 
      f.id === id ? { ...f, status: f.status === 'pending' ? 'completed' : 'pending' } : f
    );
    saveFollowUps(updatedFollowUps);
    toast.success('Status updated!');
  };

  const deleteFollowUp = (id) => {
    if (!window.confirm('Delete this follow-up?')) return;
    const updatedFollowUps = followUps.filter(f => f.id !== id);
    saveFollowUps(updatedFollowUps);
    toast.success('Follow-up removed');
  };

  const resetMeetingForm = () => {
    setMeetingForm({
      date: getNextWednesday(),
      facilitator: 'Renee',
      notes: '',
      agenda_items: [],
      action_items: []
    });
  };

  const resetFollowUpForm = () => {
    setFollowUpForm({
      assigned_to: 'Renee',
      item: '',
      due_date: getNextWednesday(),
      status: 'pending'
    });
    setEditingFollowUp(null);
  };

  const openEditMeeting = (meeting) => {
    setEditingMeeting(meeting);
    setMeetingForm({
      date: meeting.date,
      facilitator: meeting.facilitator,
      notes: meeting.notes,
      agenda_items: meeting.agenda_items || [],
      action_items: meeting.action_items || []
    });
    setDialogOpen(true);
  };

  // Get follow-ups for a specific person
  const getFollowUpsForPerson = (personName) => {
    return followUps.filter(f => 
      f.assigned_to.toLowerCase() === personName.toLowerCase() &&
      f.status === 'pending'
    );
  };

  // Get next meeting's facilitator
  const getNextFacilitator = () => {
    if (meetings.length === 0) return 'Renee';
    const lastFacilitator = meetings[0]?.facilitator;
    const currentIndex = TEAM_MEMBERS.findIndex(m => m.name.toLowerCase() === lastFacilitator?.toLowerCase());
    const nextIndex = (currentIndex + 1) % TEAM_MEMBERS.length;
    return TEAM_MEMBERS[nextIndex].name;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-4 max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-4xl font-bold font-heading tracking-tight flex items-center gap-2">
            <FileText className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 text-primary" />
            Meeting Hub
          </h1>
          <p className="text-muted-foreground text-xs sm:text-sm">
            Weekly meetings • Wednesdays
          </p>
        </div>
      </div>

      {/* Reminder Banners */}
      {isTuesday && (
        <Card className="bg-gradient-to-r from-orange-500/20 to-yellow-500/20 border-orange-500/30">
          <CardContent className="p-3">
            <div className="flex items-start gap-2">
              <Bell className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <h3 className="font-bold text-sm text-orange-400">⏰ Meeting Tomorrow!</h3>
                <p className="text-xs text-muted-foreground">
                  Complete tasks & prep. Facilitator: <strong>{getNextFacilitator()}</strong>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {isWednesday && (
        <Card className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/30">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-green-400 flex-shrink-0" />
              <div className="min-w-0">
                <span className="font-bold text-sm text-green-400">📅 Meeting Day!</span>
                <span className="text-xs text-muted-foreground ml-2">
                  Facilitator: <strong>{getNextFacilitator()}</strong>
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full grid grid-cols-3 h-auto">
          <TabsTrigger value="meetings" className="text-xs sm:text-sm py-2 px-1 sm:px-3">
            <FileText className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Meeting </span>Notes
          </TabsTrigger>
          <TabsTrigger value="followups" className="text-xs sm:text-sm py-2 px-1 sm:px-3">
            <Target className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
            Follow-Ups
          </TabsTrigger>
          <TabsTrigger value="facilitator" className="text-xs sm:text-sm py-2 px-1 sm:px-3">
            <User className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Facili</span>tator
          </TabsTrigger>
        </TabsList>

        {/* Meeting Notes Tab */}
        <TabsContent value="meetings" className="mt-4 space-y-3">
          <div className="flex justify-between items-center">
            <h2 className="text-base sm:text-lg font-semibold">Meeting History</h2>
            <Button onClick={() => { resetMeetingForm(); setEditingMeeting(null); setDialogOpen(true); }} size="sm" className="h-8 text-xs sm:text-sm">
              <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              <span className="hidden sm:inline">New </span>Meeting
            </Button>
          </div>

          {meetings.length === 0 ? (
            <Card className="border-dashed border-2">
              <CardContent className="p-8 text-center">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No meeting notes yet</p>
                <p className="text-xs text-muted-foreground mt-1">Create your first meeting notes!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {meetings.map(meeting => (
                <Card 
                  key={meeting.id} 
                  className={`cursor-pointer transition-all hover:border-primary/50 ${selectedMeeting?.id === meeting.id ? 'border-primary ring-2 ring-primary/20' : ''}`}
                  onClick={() => setSelectedMeeting(meeting)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar className="h-4 w-4 text-primary" />
                          <span className="font-semibold">
                            {new Date(meeting.date).toLocaleDateString('en-US', { 
                              weekday: 'long', 
                              month: 'long', 
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <User className="h-3 w-3" />
                          <span>Facilitator: {meeting.facilitator}</span>
                        </div>
                        {meeting.notes && (
                          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                            {meeting.notes.substring(0, 100)}...
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openEditMeeting(meeting); }}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-400" onClick={(e) => { e.stopPropagation(); handleDeleteMeeting(meeting.id); }}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Selected Meeting Detail */}
          {selectedMeeting && (
            <Card className="mt-6 bg-secondary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  Meeting Notes - {new Date(selectedMeeting.date).toLocaleDateString()}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm text-muted-foreground">Facilitator</Label>
                  <p className="font-medium">{selectedMeeting.facilitator}</p>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Notes</Label>
                  <div className="mt-2 p-4 bg-background rounded-lg whitespace-pre-wrap">
                    {selectedMeeting.notes || 'No notes recorded'}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Follow-Ups Tab */}
        <TabsContent value="followups" className="mt-4 space-y-3">
          <div className="flex justify-between items-center">
            <h2 className="text-base sm:text-lg font-semibold">Meeting Follow-Ups</h2>
            <Button onClick={() => { resetFollowUpForm(); setFollowUpDialogOpen(true); }} size="sm" className="h-8 text-xs sm:text-sm">
              <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              Add
            </Button>
          </div>

          {/* Follow-ups by Person */}
          <div className="grid gap-4">
            {TEAM_MEMBERS.map(person => {
              const personFollowUps = getFollowUpsForPerson(person.name);
              return (
                <Card key={person.name} className="overflow-hidden">
                  <CardHeader className={`py-2 px-3 ${
                    person.name === 'Renee' ? 'bg-pink-500/10' : 
                    person.name === 'Biz' ? 'bg-blue-500/10' : 
                    'bg-amber-500/10'
                  }`}>
                    <CardTitle className="text-sm flex items-center justify-between">
                      <div>
                        <span>{person.emoji} {person.name}</span>
                        <span className="text-xs text-muted-foreground ml-2 hidden sm:inline">{person.title}</span>
                      </div>
                      <Badge variant="outline" className="text-xs">{personFollowUps.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3">
                    {personFollowUps.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-2">
                        No pending follow-ups ✨
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {personFollowUps.map(followUp => (
                          <div 
                            key={followUp.id}
                            className="flex items-start justify-between p-2 bg-secondary/30 rounded-lg gap-2"
                          >
                            <div className="flex items-start gap-2 min-w-0 flex-1">
                              <button
                                onClick={() => toggleFollowUpStatus(followUp.id)}
                                className="text-muted-foreground hover:text-green-500 transition-colors mt-0.5 flex-shrink-0"
                              >
                                {followUp.status === 'completed' ? (
                                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                                ) : (
                                  <div className="h-4 w-4 rounded-full border-2 border-muted-foreground" />
                                )}
                              </button>
                              <div className="min-w-0">
                                <p className={`text-sm ${followUp.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                                  {followUp.item}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Due: {new Date(followUp.due_date).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-7 w-7 p-0"
                                onClick={() => openEditFollowUp(followUp)}
                              >
                                <Edit2 className="h-3 w-3" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-7 w-7 p-0 text-red-400"
                                onClick={() => deleteFollowUp(followUp.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Facilitator Tab */}
        <TabsContent value="facilitator" className="mt-4 space-y-3">
          <Card className="bg-gradient-to-br from-primary/10 to-pink-500/10 border-primary/20">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <User className="h-5 w-5 text-primary" />
                Facilitator Rotation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-background rounded-lg">
                <h3 className="font-semibold text-sm mb-1">Next Meeting Facilitator:</h3>
                <div className="text-2xl font-bold text-primary">{getNextFacilitator()}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Based on rotation from last meeting
                </p>
              </div>

              <div className="p-3 bg-secondary/30 rounded-lg">
                <h3 className="font-semibold text-sm mb-2">Recent Facilitators:</h3>
                <div className="space-y-1">
                  {meetings.slice(0, 5).map((meeting) => (
                    <div key={meeting.id} className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">
                        {new Date(meeting.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}:
                      </span>
                      <span className="font-medium">{meeting.facilitator}</span>
                    </div>
                  ))}
                  {meetings.length === 0 && (
                    <p className="text-xs text-muted-foreground">No meetings recorded yet</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Facilitator Checklist */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base sm:text-lg">📋 Facilitator Checklist</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              {/* Before the Meeting */}
              <div>
                <h4 className="font-semibold text-primary mb-2">Before the Meeting</h4>
                <ul className="space-y-1 text-muted-foreground">
                  <li>☐ Confirm meeting date, start time, and hard stop (4:30PM-6:30PM)</li>
                  <li>☐ Review the agenda and time blocks</li>
                  <li>☐ Identify which updates are informational vs decision-needed</li>
                  <li>☐ Prepare a visible Parking Lot section</li>
                  <li>☐ Confirm note taker (Renee) and task tracker</li>
                </ul>
              </div>

              {/* Opening */}
              <div>
                <h4 className="font-semibold text-green-500 mb-2">Opening the Meeting (First 5 Min)</h4>
                <ul className="space-y-1 text-muted-foreground">
                  <li>☐ Try to start on time</li>
                  <li>☐ State the purpose of the meeting</li>
                  <li>☐ Confirm agenda and time limit</li>
                  <li>☐ Remind: agenda-driven flow, parking lot usage, CEO confirms decisions</li>
                </ul>
              </div>

              {/* During */}
              <div>
                <h4 className="font-semibold text-blue-500 mb-2">During the Meeting</h4>
                <ul className="space-y-1 text-muted-foreground">
                  <li>☐ Keep discussion aligned to current agenda item</li>
                  <li>☐ Watch the clock - give time warnings ("2 minutes left")</li>
                  <li>☐ Redirect off-topic items to Parking Lot</li>
                  <li>☐ Ensure each update stays within its time block</li>
                  <li>☐ Pause and clarify when decisions or next steps are needed</li>
                </ul>
              </div>

              {/* Action Items */}
              <div>
                <h4 className="font-semibold text-orange-500 mb-2">Decision & Action Item Handling</h4>
                <ul className="space-y-1 text-muted-foreground">
                  <li>☐ Clearly identify when a decision is needed</li>
                  <li>☐ Confirm decision owner (CEO)</li>
                  <li>☐ Assign action items with: owner + deadline</li>
                  <li>☐ Restate action items out loud</li>
                </ul>
              </div>

              {/* Closing */}
              <div>
                <h4 className="font-semibold text-purple-500 mb-2">Closing the Meeting</h4>
                <ul className="space-y-1 text-muted-foreground">
                  <li>☐ Review action items and owners</li>
                  <li>☐ Review parking-lot items and where they'll live</li>
                  <li>☐ Confirm next meeting date/time</li>
                  <li>☐ Confirm next facilitator</li>
                  <li>☐ End on time (DO NOT extend past hard stop)</li>
                </ul>
              </div>

              {/* After */}
              <div>
                <h4 className="font-semibold text-pink-500 mb-2">After the Meeting</h4>
                <ul className="space-y-1 text-muted-foreground">
                  <li>☐ Send meeting recap within 24 hours (decisions, action items, parking lot)</li>
                  <li>☐ Update task tracker as needed</li>
                </ul>
              </div>

              {/* Reminder Box */}
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <h4 className="font-semibold text-amber-400 mb-1">💡 Facilitator Reminder</h4>
                <p className="text-xs text-muted-foreground">
                  Your role is to: <strong>protect time</strong>, <strong>maintain clarity</strong>, <strong>keep the meeting moving</strong>, and <strong>ensure decisions and next steps are captured</strong>.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  You are NOT responsible for solving every issue in the room.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New/Edit Meeting Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading">
              {editingMeeting ? 'Edit Meeting Notes' : 'New Meeting Notes'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Meeting Date</Label>
                <Input
                  type="date"
                  value={meetingForm.date}
                  onChange={(e) => setMeetingForm({ ...meetingForm, date: e.target.value })}
                />
              </div>
              <div>
                <Label>Facilitator</Label>
                <Select 
                  value={meetingForm.facilitator} 
                  onValueChange={(v) => setMeetingForm({ ...meetingForm, facilitator: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TEAM_MEMBERS.map(member => (
                      <SelectItem key={member.name} value={member.name}>
                        {member.emoji} {member.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Meeting Notes</Label>
              <Textarea
                value={meetingForm.notes}
                onChange={(e) => setMeetingForm({ ...meetingForm, notes: e.target.value })}
                placeholder="📅 VYRUS LEADERSHIP MEETING NOTES

═══════════════════════════════════════
👑 CEO UPDATE – Vision, Direction & Priorities
───────────────────────────────────────
• 
• 
• 

═══════════════════════════════════════
👨‍💼 BUSINESS PARTNER UPDATE – Sales, Trading Cards & Wholesale
───────────────────────────────────────
• 
• 
• 

═══════════════════════════════════════
👩‍💼 OPERATIONS & CONVENTIONS UPDATE
───────────────────────────────────────
• Conventions: 
• Booth/Shipping: 
• Orders: 
• Other: 

═══════════════════════════════════════
🗣️ CROSS-FUNCTIONAL DISCUSSION & CLARIFICATIONS
───────────────────────────────────────
• 
• 

═══════════════════════════════════════
✅ DECISIONS MADE
───────────────────────────────────────
• 
• 

═══════════════════════════════════════
📌 ACTION ITEMS & OWNERS
───────────────────────────────────────
• [Ozzy] - 
• [Biz] - 
• [Renee] - 

═══════════════════════════════════════
🅿️ PARKING LOT (Non-urgent/Revisit Later)
───────────────────────────────────────
• 
• 

═══════════════════════════════════════
📆 NEXT MEETING
───────────────────────────────────────
• Next Facilitator: 
• Date/Time: Wednesday @ 4:30PM
• Rolling Items: "
                className="min-h-[400px] font-mono text-sm"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setDialogOpen(false); setEditingMeeting(null); }}>
                Cancel
              </Button>
              <Button onClick={editingMeeting ? handleUpdateMeeting : handleAddMeeting}>
                <Save className="h-4 w-4 mr-2" />
                {editingMeeting ? 'Update' : 'Save'} Notes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Follow-Up Dialog */}
      <Dialog open={followUpDialogOpen} onOpenChange={(open) => { setFollowUpDialogOpen(open); if (!open) resetFollowUpForm(); }}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading">
              {editingFollowUp ? 'Edit Follow-Up' : 'Add Follow-Up Item'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Assigned To</Label>
              <Select 
                value={followUpForm.assigned_to} 
                onValueChange={(v) => setFollowUpForm({ ...followUpForm, assigned_to: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TEAM_MEMBERS.map(member => (
                    <SelectItem key={member.name} value={member.name}>
                      {member.emoji} {member.name} - {member.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Follow-Up Item</Label>
              <Textarea
                value={followUpForm.item}
                onChange={(e) => setFollowUpForm({ ...followUpForm, item: e.target.value })}
                placeholder="What needs to be followed up on?"
                className="min-h-[100px]"
              />
            </div>

            <div>
              <Label>Due Date</Label>
              <Input
                type="date"
                value={followUpForm.due_date}
                onChange={(e) => setFollowUpForm({ ...followUpForm, due_date: e.target.value })}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setFollowUpDialogOpen(false); resetFollowUpForm(); }}>
                Cancel
              </Button>
              <Button onClick={handleAddFollowUp}>
                <Save className="h-4 w-4 mr-2" />
                {editingFollowUp ? 'Update' : 'Add'} Follow-Up
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MeetingNotes;
